// Shared constants for AdminDashboard and its sub-components.
// Single source of truth for thresholds, colors, and chart configuration.

// ─── MOM & Regulatory Thresholds ───────────────────────────────────────────
export const THRESHOLDS = {
  OT_MONTHLY_LIMIT: 72,
  OT_GAUGE_MODERATE: 60,
  OT_GAUGE_CRITICAL: 80,

  TURNOVER_RAG_GREEN: 18,
  TURNOVER_RAG_AMBER: 25,
  TURNOVER_STAFF_HEALTHY: 14,
  TURNOVER_STAFF_MONITOR: 22,
  TURNOVER_WORKER_HEALTHY: 20,
  TURNOVER_WORKER_MONITOR: 30,

  RETENTION_90D_GOOD: 80,
  RETENTION_6M_GOOD: 75,

  DRC_RAG_GREEN: 3.5,
  DRC_RAG_AMBER: 4.5,
  MOM_DRC_LIMIT: 5.0,
  MOM_FOREIGN_SHARE_LIMIT: 83.3,
  DRC_COMPANY_TARGET: 3.0,

  PASS_EXPIRY_RAG_AMBER: 3,

  REST_DAY_COMPLIANCE_GREEN: 95,
  REST_DAY_COMPLIANCE_AMBER: 80,

  MOM_COMPLIANCE_RAG_AMBER: 3,

  ATTENDANCE_NORMAL_VARIATION: 2,
  ATTENDANCE_INVESTIGATE: 5,
  ATTENDANCE_GOOD: 90,
  ATTENDANCE_FAIR: 75,
};

// ─── RAG Status Styling ────────────────────────────────────────────────────
export const RAG_CARD_COLORS = {
  green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  red: 'bg-red-50 border-red-200 text-red-800',
  gray: 'bg-gray-50 border-gray-200 text-gray-600',
};

export const RAG_DOT_COLORS = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  gray: 'bg-gray-400',
};

// ─── Chart Color Palettes ──────────────────────────────────────────────────
export const CHART_COLORS_6 = ['#3B82F6', '#EF4444', '#F97316', '#10B981', '#8B5CF6', '#6366F1'];
export const CHART_COLORS_5 = ['#3B82F6', '#EC4899', '#10B981', '#F97316', '#8B5CF6'];

export const CHART_AXIS_COLOR = '#6b7280';
export const CHART_GRID_COLOR = '#e5e7eb';
export const CHART_TOOLTIP_BORDER = '#e5e7eb';

export const TURNOVER_CHART_COLORS = {
  staff: '#3b82f6',
  worker: '#f59e0b',
};

export const DRC_CHART_COLORS = {
  ttmLine: '#F59E0B',
  yoyCurrent: '#EF4444',
  yoyPrev: '#10B981',
  yoyPrev2: '#8B5CF6',
  momLimit: '#EF4444',
  companyTarget: '#1E3A8A',
};

export const COMPLIANCE_RING_COLORS = {
  green: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
};

// ─── Horizontal Bar Color Palettes (Tailwind class-based) ──────────────────
export const AGE_GROUP_COLORS = [
  { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700' },
  { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-700' },
  { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-700' },
  { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-700' },
];

export const TENURE_COLORS = [
  { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700' },
  { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-700' },
  { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-700' },
];

/** @deprecated Use getDepartmentDashboardColor() from config/departmentConfig.js */
export const DEPARTMENT_COLORS = {
  blue: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700' },
  green: { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700' },
  purple: { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-700' },
  orange: { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-700' },
  indigo: { bg: 'bg-indigo-500', light: 'bg-indigo-100', text: 'text-indigo-700' },
  teal: { bg: 'bg-teal-500', light: 'bg-teal-100', text: 'text-teal-700' },
  amber: { bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-700' },
  rose: { bg: 'bg-rose-500', light: 'bg-rose-100', text: 'text-rose-700' },
};

export const RETIREMENT_TIERS = [
  { label: '1-Year (62+)', key: '1yr', color: 'bg-red-500', lightColor: 'bg-red-50', textColor: 'text-red-700' },
  { label: '3-Year (60+)', key: '3yr', color: 'bg-orange-500', lightColor: 'bg-orange-50', textColor: 'text-orange-700' },
  { label: '5-Year (58+)', key: '5yr', color: 'bg-yellow-500', lightColor: 'bg-yellow-50', textColor: 'text-yellow-700' },
];

// ─── Shared Tooltip Style ──────────────────────────────────────────────────
export const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'white',
  border: `1px solid ${CHART_TOOLTIP_BORDER}`,
  borderRadius: '8px',
  padding: '8px',
};

// ─── Work Pass Expiry Timeline Colors ──────────────────────────────────────
export const PASS_EXPIRY_SEGMENTS = [
  { key: 'expired', color: 'bg-red-500', label: 'Expired' },
  { key: 'within_30', color: 'bg-orange-500', label: '≤30d' },
  { key: 'within_60', color: 'bg-amber-400', label: '31-60d' },
  { key: 'within_90', color: 'bg-yellow-300', label: '61-90d' },
  { key: 'valid', color: 'bg-emerald-500', label: '>90d' },
];

// ─── Alert Severity Styling ────────────────────────────────────────────────
export const ALERT_SEVERITY = {
  critical: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-800', label: 'Critical' },
  warning: { dot: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800', label: 'Warning' },
  monitor: { dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800', label: 'Monitor' },
};
